import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchROIMediaTypeData } from 'hooks/portfolioAnalysis.tsx/fast_api/useFetchMediaTypeData';
import { useFetchMediaTypes } from 'hooks/portfolioAnalysis.tsx/fast_api/useFetchMediaTypes';
import { getPortfolioAnalysisMediaTableColumns } from 'utils/getPortfolioAnalysisColumns';
import { calculateContainerHeight, getAdditionalDataOnCopyClick, reverseObjectKeyValues } from 'utils/helperMethods';
import Legend from 'components/molecules/Legend';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { trackEventMixpanel } from 'utils/mixpanel';
import { getRoiMediaExportData } from 'parser/graphToExcelDownloadParser';
import {
    PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION,
    PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION_EXPORT,
} from 'utils/mixpanelEventNames';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { legendData } from 'components/pages/PortfolioAnalysis';

type props = {
    tableRef?: any;
};

const ROIMediaSpends = ({ tableRef }: props) => {
    const offsetRef = useRef(0);
    const { appConfig: config } = useContext(UserContext);

    const {
        timePeriodSelection,
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allCheckedAttribute,
        timeSelectionLabel,
    } = useContext(FilterContextData);
    const { limit, mediaSpendsAttributeOptions, mediaSpendsLabelKeyMapping } = config.portfolioAnalysis;
    const [offset, setOffSet] = useState<number>(0);
    const [mediaTypesState, setMediaTypesState] = useState<any>({});
    const [selectedAttribute, setSelectedAttribute] = useState<any>(mediaSpendsAttributeOptions[0]);
    const [openAttributeDropdown, setOpenAttributeDropdown] = useState(false);
    const portfolioAnalysisDataError = false;

    const commonLoading = false;
    const dataSource: any = [];
    const height = calculateContainerHeight(1, 64 + 16 * 3);

    const filtersAndTimePeriodVariables: any = useMemo(() => {
        return {
            time_period: JSON.parse(timePeriodSelection),
            filters: idForRestOfComponent,
        };
    }, [timePeriodSelection, idForRestOfComponent]);

    const mediaDataVariables = useMemo(() => {
        if (!idForRestOfComponentLoading) {
            return {
                ...filtersAndTimePeriodVariables,
                limit: limit,
                offset: offset,
                media_list:
                    mediaTypesState['Media Type']
                        ?.slice(1)
                        ?.filter((m: any) => m.selected)
                        .map((m: any) => m.key) || [],
                attribute_type: selectedAttribute.key,
                dimension: selectedAttribute.dim,
            };
        }
        return null;
    }, [filtersAndTimePeriodVariables, idForRestOfComponentLoading, offset, mediaTypesState, selectedAttribute]);

    const { loading: mediaTypesLoading } = useFetchMediaTypes({
        queryKey: ['roi-media-types', filtersAndTimePeriodVariables],
        variables: filtersAndTimePeriodVariables,
        queryConfig: {},
        setMediaTypesState,
    });

    const {
        loading: mediaTypesDataLoading,
        data: mediaTypesData,
        count,
    } = useFetchROIMediaTypeData({
        queryKey: ['roi-media-data', mediaDataVariables],
        variables: mediaDataVariables,
        queryConfig: {
            enabled: !idForRestOfComponentLoading && !mediaTypesLoading,
        },
        attributeName: selectedAttribute.label,
        offset: offset,
    });

    useEffect(() => {
        setOffSet(0);
    }, [selectedAttribute, mediaTypesState]);

    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newOffset = offsetRef.current + limit;
            offsetRef.current = newOffset;
            setOffSet(newOffset);
        }
    };
    const headerKeys = Object.keys(reverseObjectKeyValues(mediaSpendsLabelKeyMapping));

    const exportData = useMemo(() => {
        const headerValue = Object.keys(mediaTypesData?.[0] || {});
        const headers = [headerValue[0], ...headerKeys.slice(1)];
        const result = getRoiMediaExportData(mediaTypesData, headers);
        return { headers, result };
    }, [mediaTypesData]);

    // updating the first column name to attribute name
    headerKeys[0] = selectedAttribute.label;

    const memoisedTableColumns = useMemo(() => {
        return getPortfolioAnalysisMediaTableColumns(
            [],
            mediaTypesState,
            setMediaTypesState,
            headerKeys,
            mediaSpendsAttributeOptions,
            selectedAttribute,
            setSelectedAttribute,
            openAttributeDropdown,
            setOpenAttributeDropdown,
        );
    }, [mediaTypesState, selectedAttribute, openAttributeDropdown, headerKeys]);

    if (mediaTypesLoading) {
        return (
            <div className="flex justify-center items-center w-full h-96 ">
                <Spin />
            </div>
        );
    }

    return (
        <div className="mt-4 relative" id="portfolio-analysis-infinite-scroller">
            <div className="flex items-center gap-4 absolute right-0 top-[-44px]">
                <Legend data={legendData} />
                <CopyToClipBoard
                    mixpanelEvent={() => trackEventMixpanel(PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION)}
                    refOfComponent={tableRef}
                />
                <DownloadExcel
                    mixpanelEvent={() =>
                        trackEventMixpanel(PORTFOLIO_ANALYSIS_TABLE_MICRO_INTERACTION_EXPORT, {
                            Table_Name: 'Portfolio Analysis Table Data',
                        })
                    }
                    title={'Media Table Data'}
                    excelData={exportData.result}
                    headers={exportData.headers}
                />
            </div>

            <InfiniteScroller
                data={dataSource || []}
                loading={commonLoading}
                hasMore={offsetRef.current + limit < count || false}
                height={dataSource && !portfolioAnalysisDataError ? height : 'auto'}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    pagination={false}
                    columns={memoisedTableColumns}
                    dataSource={mediaTypesData || []}
                    scroll={{ x: '100%' }}
                    sticky
                    loading={mediaTypesLoading || mediaTypesDataLoading}
                />
            </InfiniteScroller>
        </div>
    );
};

export default ROIMediaSpends;
